import { Alert, Box, Button } from "@mui/material";
import { useEffect, useRef } from "react";
import { SnackbarAlert } from "../../common/SnackbarAlert";
import { useExtendedLoading } from "../../shared/hooks/useExtendedLoading";
import { JobLoadingStatus } from "./JobLoadingStatus";
import { JobNextPrev } from "./JobNextPrev";
import { JobStepper } from "./JobStepper";
import { useDispatch } from "react-redux";
import {
  resetJobCreateStepper,
  selectService,
  setClient,
  setQuote,
  setScopeOfWork,
  setScopeOfWorkMode,
  setStep,
} from "../../../redux/features/jobCreateSlice";
import { getTimelineClickedTime } from "../config/timeline.constants";
import { ScopeOfWorkHandle } from "../../shared/components/features/scope-of-work/controller/scope-of-work.handle";
import { buildJobCreateSteps } from "./builder/buildJobCreateSteps";
import { useJobsCreateController } from "./hooks/useJobsCreate";
import { useJobWizardHandlers } from "./hooks/useJobWizardHandlers";
import { useAppSelector } from "src/redux/hooks/redux";
import { useJobCreateWindowStore } from "./store/useJobCreateWindowStore";
import { useJobCreateDraftStore } from "./store/useJobCreateDraftStore";
import { selectActiveCompanyId } from "../../../redux/features/user/selectors/userState.selectors";
import { useClients } from "../../clients/hooks/useClients";
import { listCoverageAreas, withResolvedCoverage } from "./helper/resolvePreferredCoverageArea";

interface JobsCreateProps {
  withQuoteService?: boolean;
  clientUidSelected?: string | null;
  isUpdateJob?: boolean;
  jobId?: string | number | null;
  skipClientStep?: boolean;
}

export const JobsCreate = ({
  withQuoteService = false,
  clientUidSelected = null,
  isUpdateJob = false,
  jobId = null,
  skipClientStep = false,
}: JobsCreateProps) => {
  const { state, loading, snackbar, closeSnackbar, isBasic, handleCreateJob, scheduleLater,
    unconfirmedScheduleJobId, retryUnconfirmedSchedule, isRetryingSchedule, scheduleRecovered,
    canRetryUnconfirmedSchedule, markPendingJobReviewed, reviewedPendingJob } = useJobsCreateController({
    modalKey: clientUidSelected,
    isUpdateJob,
    jobId,
  });

  const { handleScopeOfWork, handleEventScheduler } = useJobWizardHandlers();
  const dispatch = useDispatch();
  const { getClientByUid } = useClients();
  const activeCompanyId = useAppSelector(selectActiveCompanyId);

  const scopeOfWorkRef = useRef<ScopeOfWorkHandle>(null);
  const selectedClientRef = useRef(state.client);
  selectedClientRef.current = state.client;
  const restoredStepRef = useRef(false);
  const skipNextStepSaveRef = useRef(false);

  const savedStep = useJobCreateWindowStore((store) => store.step);
  const savedQuoteMode = useJobCreateWindowStore((store) => store.quoteMode);
  const savedWindowCompanyId = useJobCreateWindowStore((store) => store.companyId);
  const hasWindowStoreHydrated = useJobCreateWindowStore((store) => store.hasHydrated);
  const setWindowState = useJobCreateWindowStore((store) => store.setWindowState);

  const savedDraftCompanyId = useJobCreateDraftStore((store) => store.companyId);
  const savedScopeOfWork = useJobCreateDraftStore((store) => store.scopeOfWork);
  const savedQuote = useJobCreateDraftStore((store) => store.quote);
  const savedSelectedService = useJobCreateDraftStore((store) => store.selectedService);
  const hasDraftStoreHydrated = useJobCreateDraftStore((store) => store.hasHydrated);
  const setDraft = useJobCreateDraftStore((store) => store.setDraft);

  const canProceed = useAppSelector((state) => state.jobCreateStepper.skipClientSelection);
  const coverageAreas = listCoverageAreas(state.client);
  const clientMatchesSelection =
    Boolean(clientUidSelected) &&
    state.client &&
    typeof state.client === "object" &&
    state.client.uid === clientUidSelected &&
    Array.isArray(state.client.coverageAreas);
  const needsAddressChoice = Boolean(clientUidSelected) && !withQuoteService && clientMatchesSelection && coverageAreas.length > 1;
  const waitingForClientAddresses = Boolean(clientUidSelected) && !withQuoteService && !clientMatchesSelection;

  const baseStepContext = {
    withQuoteService,
    isBasic,
    scheduleLater,
    ref: scopeOfWorkRef,
    scopeOfWorkState: state.scopeOfWork,
    onStateEventSchedulerChange: handleEventScheduler,
    eventSchedulerState: state.eventScheduler,
    onStateScopeOfWorkChange: handleScopeOfWork,
    selectedService: state.selectedService,
    clientUidSelected,
    skipClientStep,
    needsAddressChoice,
    isUpdateJob,
    jobId,
  };

  const baseSteps = buildJobCreateSteps(baseStepContext);

  const goToStepByKey = (
    stepKey: string,
    options?: {
      returnToSummary?: boolean;
      scopeStep?: string;
    },
  ) => {
    const stepIndex = baseSteps.config.findIndex((step) => step.stepKey === stepKey);

    if (stepIndex === -1) return;

    if (options?.returnToSummary) {
      if (!sessionStorage.getItem("jobCreateEditSnapshot")) {
        sessionStorage.setItem("jobCreateEditSnapshot", JSON.stringify(state));
      }

      sessionStorage.setItem("jobCreateReturnToSummary", "true");
    }

    if (options?.scopeStep) {
      sessionStorage.setItem("jobCreateScopeStep", options.scopeStep);
    }

    dispatch(setStep(stepIndex));
  };

  const { components, config, stepperConfig } = buildJobCreateSteps({
    ...baseStepContext,
    goToStepByKey,
  });

  const showLoading = useExtendedLoading(loading, isUpdateJob ? 0 : 1500);
  const CurrentStepComponent = components[state.step];
  const isSavedWindowForActiveCompany =
    savedWindowCompanyId != null && activeCompanyId != null && String(savedWindowCompanyId) === String(activeCompanyId);
  const isSavedDraftForActiveCompany =
    savedDraftCompanyId != null && activeCompanyId != null && String(savedDraftCompanyId) === String(activeCompanyId);

  useEffect(() => {
    if (restoredStepRef.current || !hasWindowStoreHydrated || !hasDraftStoreHydrated || savedStep == null || !isSavedWindowForActiveCompany) {
      return;
    }

    restoredStepRef.current = true;
    skipNextStepSaveRef.current = true;

    if (savedQuoteMode && !state.quoteMode) {
      dispatch(setScopeOfWorkMode(savedQuoteMode));
    }

    if (isSavedDraftForActiveCompany && savedScopeOfWork && !state.scopeOfWork) {
      dispatch(setScopeOfWork(savedScopeOfWork));
    }

    if (isSavedDraftForActiveCompany && savedQuote && !state.quote) {
      dispatch(setQuote(savedQuote));
    }

    if (isSavedDraftForActiveCompany && savedSelectedService && !state.selectedService) {
      dispatch(selectService(savedSelectedService));
    }

    dispatch(setStep(Math.min(savedStep, components.length - 1)));
  }, [
    components.length,
    dispatch,
    hasDraftStoreHydrated,
    hasWindowStoreHydrated,
    isSavedDraftForActiveCompany,
    isSavedWindowForActiveCompany,
    savedQuote,
    savedQuoteMode,
    savedDraftCompanyId,
    savedScopeOfWork,
    savedSelectedService,
    savedStep,
    savedWindowCompanyId,
    state.quote,
    state.quoteMode,
    state.scopeOfWork,
    state.selectedService,
  ]);

  useEffect(() => {
    if (!hasWindowStoreHydrated || !hasDraftStoreHydrated) return;

    if (skipNextStepSaveRef.current) {
      skipNextStepSaveRef.current = false;
      return;
    }

    setWindowState({
      step: state.step,
      quoteMode: state.quoteMode,
      companyId: activeCompanyId,
    });

    setDraft({
      companyId: activeCompanyId,
      scopeOfWork: state.scopeOfWork,
      quote: state.quote,
      selectedService: state.selectedService,
    });
  }, [
    hasDraftStoreHydrated,
    hasWindowStoreHydrated,
    activeCompanyId,
    setDraft,
    setWindowState,
    state.quote,
    state.quoteMode,
    state.scopeOfWork,
    state.selectedService,
    state.step,
  ]);

  useEffect(() => {
    if (!clientUidSelected) return undefined;

    const currentClient = selectedClientRef.current;
    const hydrated =
      currentClient &&
      typeof currentClient === "object" &&
      currentClient.uid === clientUidSelected &&
      Array.isArray(currentClient.coverageAreas);

    if (hydrated) return undefined;

    let cancelled = false;

    if (!(typeof currentClient === "string" && currentClient === clientUidSelected)) {
      dispatch(setClient(clientUidSelected));
    }

    getClientByUid(clientUidSelected).then(({ data, ok }) => {
      if (cancelled || !ok || !data) return;

      const latestClient = selectedClientRef.current;
      const keptCoverage =
        latestClient && typeof latestClient === "object" && latestClient.uid === clientUidSelected && latestClient.coverageArea?.id != null
          ? latestClient.coverageArea
          : undefined;

      dispatch(
        setClient(
          withResolvedCoverage({
            ...data,
            ...(keptCoverage ? { coverageArea: keptCoverage } : {}),
          }),
        ),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [clientUidSelected, dispatch, getClientByUid, state.client]);

  useEffect(() => {
    if (!needsAddressChoice) return;
    const hasAddress = state.client && typeof state.client === "object" && state.client.coverageArea?.id != null;
    if (hasAddress) return;

    const addressStepIndex = config.findIndex((step) => step.stepKey === "selectClientAddress");
    if (addressStepIndex === -1 || state.step === addressStepIndex) return;

    dispatch(setStep(addressStepIndex));
  }, [config, dispatch, needsAddressChoice, state.client, state.step]);

  useEffect(() => {
    return () => {
      if (getTimelineClickedTime()) return;
      dispatch(resetJobCreateStepper());
    };
  }, [dispatch]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}>
      <JobStepper stepsConfig={stepperConfig} currStep={state.step} showCurrent />

      <Box sx={{ flex: 1, position: "relative", overflow: "auto" }}>
        {(showLoading || waitingForClientAddresses) && <JobLoadingStatus />}
        <Box sx={{ opacity: showLoading || waitingForClientAddresses ? 0.4 : 1, pointerEvents: waitingForClientAddresses ? "none" : "auto" }}>
          {CurrentStepComponent}
        </Box>
      </Box>

      {unconfirmedScheduleJobId && <Alert severity={scheduleRecovered ? "success" : "warning"} data-qa-unconfirmed-schedule sx={{ mt: 1 }}>
        {scheduleRecovered
          ? `The schedule for job #${unconfirmedScheduleJobId} is confirmed. Open it in Jobs to review it.`
          : reviewedPendingJob
            ? `Job #${unconfirmedScheduleJobId} remains unconfirmed. Close this form before creating a different job.`
          : `Job #${unconfirmedScheduleJobId} was created, but its schedule could not be confirmed. Do not create it again; review it in Jobs before assigning work.`}
        {!scheduleRecovered && !reviewedPendingJob && canRetryUnconfirmedSchedule && <Button size="small" onClick={retryUnconfirmedSchedule} disabled={isRetryingSchedule} data-qa-retry-schedule>
          {isRetryingSchedule ? "Checking schedule…" : "Retry schedule for this job"}
        </Button>}
        {!scheduleRecovered && !reviewedPendingJob && <Button size="small" onClick={markPendingJobReviewed} data-qa-reviewed-job>
          I reviewed this job in Jobs
        </Button>}
      </Alert>}

      {!waitingForClientAddresses && (
        <JobNextPrev
          totalSteps={components.length}
          stepsConfig={config}
          scopeOfWorkRef={scopeOfWorkRef}
          handleCreateJob={handleCreateJob}
          isClientSkipped={canProceed}
          isUpdateJob={isUpdateJob}
          blockedCreateJob={Boolean(unconfirmedScheduleJobId)}
        />
      )}

      <SnackbarAlert openState={snackbar.open} setOpenState={closeSnackbar} severity={snackbar.severity} message={snackbar.message} />
    </Box>
  );
};
